import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './DefenderDetailModal.css';

const API_BASE = 'https://rangers-api-3mfw.onrender.com';

const ELEMENT_META = {
  fire:  { label: '火', color: '#ff7a5c' },
  water: { label: '水', color: '#5cb3ff' },
  tree:  { label: '木', color: '#5ee08a' },
  light: { label: '光', color: '#ffd95c' },
  dark:  { label: '闇', color: '#b48cff' },
};

const CATEGORY_META = {
  STR: { label: '体力', color: '#ff7a6b' },
  AGI: { label: '敏捷', color: '#5ee08a' },
  INT: { label: '魔法', color: '#5cb3ff' },
};

const fmt = (v) => (v == null ? '-' : Number(v).toLocaleString('ja-JP'));
const pct = (v) => (v == null ? '-' : `${(Number(v) * 100).toFixed(1)}%`);

const skillTextCache = new Map();

async function fetchSkillTexts(keys, authToken) {
  const missing = [...new Set(keys)].filter((k) => k && !skillTextCache.has(k));
  if (missing.length === 0) return;
  const res = await axios.post(
    `${API_BASE}/api/skill-text`,
    { keys: missing },
    { headers: { Authorization: `Bearer ${authToken}` } }
  );
  const map = res.data || {};
  for (const key of missing) {
    skillTextCache.set(key, map[key] || '');
  }
}

function collectSkillKeys(units) {
  const keys = [];
  for (const u of units) {
    const { skills, skills3 } = getPrimarySkills(u);
    for (const sk of [...skills, ...skills3]) {
      const nameKey = sk.nameCode || (sk.skillCode ? `${sk.skillCode}_nm` : '');
      const descKey = sk.descriptionCode || (sk.skillCode ? `${sk.skillCode}_desc` : '');
      if (nameKey) keys.push(nameKey);
      if (descKey) keys.push(descKey);
    }
  }
  return keys;
}

function parseSkillDesc(desc) {
  if (!desc) return { flavor: '', effects: [] };
  // lericoの翻訳は実改行とリテラル "\n" が混在する
  const normalized = String(desc).replace(/\\n/g, '\n');
  const lines = normalized.split('\n');
  const effects = [];
  const flavorLines = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('*')) {
      effects.push(line.replace(/^\*+\s*/, '').trim());
    } else {
      flavorLines.push(line);
    }
  }
  return { flavor: flavorLines.join(''), effects };
}

// skills配列は本スキル + サブ効果(_a/_b/_c)が並ぶ。
// nameCode/descriptionCode がある本スキルだけ表示（説明文に全効果が含まれる）
function getPrimarySkills(unit) {
  const primary = (list) =>
    (list || []).filter((sk) => sk && (sk.nameCode || sk.descriptionCode));
  return {
    skills: primary(unit.skills),
    skills3: primary(unit.skills3),
  };
}

function resolveSkillText(sk, skillTexts) {
  const nameKey = sk.nameCode || (sk.skillCode ? `${sk.skillCode}_nm` : '');
  const descKey = sk.descriptionCode || (sk.skillCode ? `${sk.skillCode}_desc` : '');
  return {
    name: (nameKey && skillTexts[nameKey]) || '',
    desc: (descKey && skillTexts[descKey]) || '',
    nameKey,
    descKey,
  };
}

const SWITCH_TEXTS_11 = { '01':'物攻','02':'命中','03':'ス発','04':'ス命','05':'攻ス','06':'体力','07':'回避','08':'ス回','09':'ス抵','10':'物反','11':'魔反','12':'ﾉｯｸ','13':'ｽﾀﾝ','14':'沈黙','15':'移動','16':'攻範','17':'ミネ','18':'再出' };
const SWITCH_TEXTS_12 = { '01':'物攻','02':'命中','03':'決確','04':'決ダ','05':'ス発','06':'ス命','07':'攻ス','08':'体力','09':'回避','10':'ス回','11':'ス抵','12':'ﾉｯｸ','13':'ｽﾀﾝ','14':'沈黙','15':'移動','16':'攻範','17':'ミネ','18':'再出' };
const SWITCH_TEXTS_13 = { '01':'魔攻','02':'命中','03':'ス発','04':'ス命','05':'攻ス','06':'物防','07':'魔防','08':'体力','09':'回避','10':'ス回','11':'ス抵','12':'ﾉｯｸ','13':'ｽﾀﾝ','14':'沈黙','15':'移動','16':'攻範','17':'ミネ','18':'再出' };
const SWITCH_TEXTS_14 = { '01':'物攻','02':'魔攻','03':'命中','04':'ス発','05':'ス命','06':'攻ス','07':'体力','08':'回避','09':'ス回','10':'ス抵','11':'ﾉｯｸ','12':'ｽﾀﾝ','13':'沈黙','14':'移動','15':'攻範','16':'ミネ','17':'再出','18':'属強','19':'属命' };

function getEquipSwitchText(eq) {
  if (!eq || !eq.attr4No) return '';
  const s = String(eq.attr4No);
  const prefix = s.substring(0, 2);
  const suffix = s.substring(4, 6);
  let table = null;
  if (prefix === '11') table = SWITCH_TEXTS_11;
  else if (prefix === '12') table = SWITCH_TEXTS_12;
  else if (prefix === '13') table = SWITCH_TEXTS_13;
  else if (['14','15','16','17','18'].includes(prefix)) table = SWITCH_TEXTS_14;
  if (!table) return '';
  const label = table[suffix];
  if (!label) return '';
  return `${label}+${eq.currentAdvancedEnchant ?? '?'}`;
}

const StatBar = ({ label, value, max, color, display }) => {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = requestAnimationFrame(() => setWidth(Math.min(100, (value / max) * 100)));
    return () => cancelAnimationFrame(t);
  }, [value, max]);
  return (
    <div className="ddm-stat-row">
      <span className="ddm-stat-label">{label}</span>
      <div className="ddm-stat-track">
        <div className="ddm-stat-fill" style={{ width: `${width}%`, background: color }} />
      </div>
      <span className="ddm-stat-value">{display}</span>
    </div>
  );
};

const SkillCard = ({ sk, kind, skillTexts }) => {
  const { name, desc } = resolveSkillText(sk, skillTexts);
  const { flavor, effects } = parseSkillDesc(desc);
  const prob = sk.probability != null ? Number(sk.probability) * 100 : null;
  const probClass = prob == null ? '' : prob >= 80 ? 'ddm-prob-high' : prob >= 50 ? 'ddm-prob-mid' : 'ddm-prob-low';
  const isHyper = kind === 'hyper';
  const displayName = name || (skillTexts && Object.keys(skillTexts).length === 0 ? 'スキル読込中…' : sk.skillCode || '不明なスキル');

  return (
    <div className={`ddm-skill-card ${isHyper ? 'ddm-skill-card-hyper' : ''}`}>
      <div className="ddm-skill-head">
        {sk.iconResourcePath && (
          <img
            className="ddm-skill-icon"
            src={`https://rangers.lerico.net/res/skill_icon/${sk.iconResourcePath}`}
            alt=""
            loading="lazy"
          />
        )}
        <div className="ddm-skill-head-text">
          <span className="ddm-skill-name">{displayName}</span>
          <span className={`ddm-skill-kind ${isHyper ? 'ddm-skill-hyper' : ''}`}>
            {isHyper ? 'ハイパー' : 'スキル'}
          </span>
        </div>
        {prob != null && (
          <span className={`ddm-skill-prob ${probClass}`}>発動率 {prob.toFixed(1)}%</span>
        )}
      </div>
      {flavor && <p className="ddm-skill-flavor">{flavor}</p>}
      {effects.length > 0 && (
        <ul className="ddm-skill-effects">
          {effects.map((ef, i) => (
            <li key={i}>{ef}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UnitRow = ({ unit, index, skillTexts }) => {
  const [open, setOpen] = useState(false);
  const elem = ELEMENT_META[unit.unitElement] || { label: '?', color: '#8fa3bf' };
  const cat = CATEGORY_META[unit.unitCategoryType] || { label: '?', color: '#8fa3bf' };
  const potTotal =
    (unit.potentialGrowthStatLevel1 || 0) +
    (unit.potentialGrowthStatLevel2 || 0) +
    (unit.potentialGrowthStatLevel3 || 0) +
    (unit.potentialGrowthStatLevel4 || 0) +
    (unit.potentialGrowthStatLevel5 || 0);

  return (
    <div className={`ddm-unit ${open ? 'ddm-unit-open' : ''}`}>
      <button className="ddm-unit-head" onClick={() => setOpen(!open)}>
        <span className="ddm-unit-slot">{index + 1}</span>
        <img
          className="ddm-unit-img"
          src={`https://rangers.lerico.net/res/${unit.unitCode}/${unit.unitCode}-thum.png`}
          alt={unit.unitCode}
          loading="lazy"
        />
        <span className="ddm-unit-name">{unit.unitName || unit.unitCode}</span>
        <span className="ddm-unit-lv">Lv{unit.unitLevel}</span>
        <span className="ddm-elem-dot" style={{ background: elem.color }} title={elem.label}>{elem.label}</span>
        <span className="ddm-cat-badge" style={{ color: cat.color, borderColor: cat.color }}>{cat.label}</span>
        <span className="ddm-unit-pot">{potTotal}</span>
        <span className={`ddm-unit-chevron ${open ? 'ddm-chevron-open' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="ddm-unit-body">
          <div className="ddm-unit-stats">
            <div className="ddm-kv"><span>物理攻撃</span><b className="ddm-v-atk">{fmt(unit.attack)}</b></div>
            <div className="ddm-kv"><span>魔法攻撃</span><b className="ddm-v-atk">{fmt(unit.specialAttack)}</b></div>
            <div className="ddm-kv"><span>体力</span><b className="ddm-v-hp">{fmt(unit.hp)}</b></div>
            <div className="ddm-kv"><span>物理防御</span><b>{fmt(unit.defence)}</b></div>
            <div className="ddm-kv"><span>魔法防御</span><b>{fmt(unit.specialDefence)}</b></div>
            <div className="ddm-kv"><span>クリティカル確率</span><b>{pct(unit.criticalProbability)}</b></div>
            <div className="ddm-kv"><span>クリティカル倍率</span><b>{unit.criticalRatio != null ? Number(unit.criticalRatio).toFixed(2) : '-'}</b></div>
            <div className="ddm-kv"><span>回避率</span><b>{pct(unit.avoidProbability)}</b></div>
            <div className="ddm-kv"><span>スキル回避率</span><b>{pct(unit.skillAvoidProbability)}</b></div>
            <div className="ddm-kv"><span>スキル抵抗</span><b>{pct(unit.skillResistance)}</b></div>
            <div className="ddm-kv"><span>命中率</span><b>{pct(unit.antiAvoidProb)}</b></div>
            <div className="ddm-kv"><span>スキル命中率</span><b>{pct(unit.antiSkillAvoidProb)}</b></div>
            <div className="ddm-kv"><span>ノックバック抵抗</span><b>{pct(unit.antiKnockbackProb)}</b></div>
            <div className="ddm-kv"><span>スタン抵抗</span><b>{pct(unit.antiStunProb)}</b></div>
            <div className="ddm-kv"><span>ダメージ反射</span><b>{pct(unit.damageReflectRatio)}</b></div>
            <div className="ddm-kv"><span>召喚エナジー</span><b className="ddm-v-en">{fmt(unit.summonEnergy)}</b></div>
            <div className="ddm-kv"><span>再生成速度</span><b>{unit.productionSpeed != null ? Number(unit.productionSpeed).toFixed(2) : '-'}</b></div>
            <div className="ddm-kv"><span>射程距離</span><b>{fmt(unit.attackRange)}</b></div>
            <div className="ddm-kv"><span>攻撃速度</span><b>{fmt(unit.attackSpeed)}</b></div>
            <div className="ddm-kv"><span>移動速度</span><b>{fmt(unit.movingSpeed)}</b></div>
            <div className="ddm-kv"><span>攻撃範囲</span><b>{unit.attackScope != null ? Number(unit.attackScope).toFixed(1) : '-'}</b></div>
            <div className="ddm-kv"><span>才能</span><b>{unit.talentGrade || 0}</b></div>
          </div>

          <div className="ddm-equip-block">
            <span className="ddm-skill-title">装備</span>
            <div className="ddm-equip-list">
              {['WEAPON', 'ARMOR', 'ACC'].map((slot) => {
                const eq = unit.equipMap?.[slot];
                if (!eq) return null;
                return (
                  <div key={slot} className="ddm-equip-card">
                    <a href={`https://rangers.lerico.net/ja/equipment/${eq.equipItemCode}`} target="_blank" rel="noopener noreferrer">
                      <img
                        className="ddm-equip-icon"
                        src={`https://rangers.lerico.net/res/gear_icon/${eq.equipItemCode}_icon.png`}
                        alt={slot}
                        loading="lazy"
                      />
                    </a>
                    <div className="ddm-equip-info">
                      <span className="ddm-equip-slot">{slot === 'WEAPON' ? '武器' : slot === 'ARMOR' ? '防具' : 'ｱｸｾ'}</span>
                      <span className="ddm-equip-grade">★{eq.currentGrade}</span>
                      <span className="ddm-equip-enchant">+{eq.currentEnchant}</span>
                      {eq.currentAdvancedEnchant > 0 && <span className="ddm-equip-adv">上+{eq.currentAdvancedEnchant}</span>}
                      {eq.currentSkillPlusEnchant > 0 && <span className="ddm-equip-skillplus">ｽｷﾙ+{eq.currentSkillPlusEnchant}</span>}
                      <span className="ddm-equip-switch">{getEquipSwitchText(eq)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="ddm-skill-block">
            <span className="ddm-skill-title">スキル</span>
            <div className="ddm-skill-list">
              {(() => {
                const { skills, skills3 } = getPrimarySkills(unit);
                if (skills.length === 0 && skills3.length === 0) {
                  return <p className="ddm-skill-empty">スキル情報なし</p>;
                }
                return (
                  <>
                    {skills.map((sk, si) => (
                      <SkillCard key={`s1-${si}`} sk={sk} kind="skill" skillTexts={skillTexts} />
                    ))}
                    {skills3.map((sk, si) => (
                      <SkillCard key={`s3-${si}`} sk={sk} kind="hyper" skillTexts={skillTexts} />
                    ))}
                  </>
                );
              })()}
            </div>
          </div>

          <div className="ddm-pot-row">
            <span className="ddm-pot-label">潜在能力</span>
            {[1, 2, 3, 4, 5].map((i) => {
              const v = unit[`potentialGrowthStatLevel${i}`] || 0;
              return (
                <div key={i} className="ddm-pot-cell">
                  <div className="ddm-pot-track">
                    <div className="ddm-pot-fill" style={{ width: `${(v / 500) * 100}%` }} />
                  </div>
                  <span>{v}</span>
                </div>
              );
            })}
          </div>

          <div className="ddm-unit-meta">
            {unit.awakeYn && <span className="ddm-meta-chip ddm-chip-awake">覚醒</span>}
            {unit.masterRanger && <span className="ddm-meta-chip ddm-chip-master">マスター</span>}
            {unit.superMasterRanger && <span className="ddm-meta-chip ddm-chip-super">超マスター</span>}
            {unit.flightYn && <span className="ddm-meta-chip">飛行</span>}
            {unit.isAdventUnit && <span className="ddm-meta-chip">降臨</span>}
            <span className="ddm-meta-chip">グレード {unit.grade}</span>
          </div>

          <a
            className="ddm-unit-link"
            href={`https://rangers.lerico.net/ja/ranger/${unit.unitCode}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            レンジャーズブックで見る ↗
          </a>
        </div>
      )}
    </div>
  );
};

const DefenderDetailModal = ({ defender, towerLabel, authToken, onClose }) => {
  const [tab, setTab] = useState('tower');
  const [skillTexts, setSkillTexts] = useState({});

  const deck = defender?.deckInfoJson || {};
  const units = deck.playerUnitTeams?.['1'] || [];

  const skillKeys = useMemo(() => {
    const u = defender?.deckInfoJson?.playerUnitTeams?.['1'] || [];
    return collectSkillKeys(u);
  }, [defender]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  useEffect(() => {
    if (!authToken || skillKeys.length === 0) return;
    let cancelled = false;

    const fromCache = {};
    skillKeys.forEach((k) => {
      if (skillTextCache.has(k)) fromCache[k] = skillTextCache.get(k);
    });
    if (Object.keys(fromCache).length > 0) setSkillTexts(fromCache);

    fetchSkillTexts(skillKeys, authToken)
      .then(() => {
        if (cancelled) return;
        const updated = {};
        skillKeys.forEach((k) => {
          if (skillTextCache.has(k)) updated[k] = skillTextCache.get(k);
        });
        setSkillTexts(updated);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [authToken, skillKeys]);

  if (!defender) return null;

  const p = defender.mergedGuildPlayer || {};
  const machine = deck.machine || {};
  const upgrade = deck.playerUpgradeStat || {};
  const upgradeLv = deck.upgradeLevelStatus || {};
  const score = defender.strengthScore;

  const lastAccess = p.lastAccessDate
    ? new Date(p.lastAccessDate).toLocaleString('ja-JP', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
  const admitted = p.admittedDate
    ? new Date(p.admittedDate).toLocaleDateString('ja-JP', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
    : '-';

  const scoreClass =
    score == null
      ? ''
      : score >= 75
        ? 'ddm-score-danger'
        : score >= 60
          ? 'ddm-score-strong'
          : score >= 45
            ? 'ddm-score-average'
            : 'ddm-score-weak';

  return (
    <div className="ddm-overlay" onClick={onClose}>
      <div className="ddm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ddm-close" onClick={onClose} aria-label="閉じる">
          ✕
        </button>

        <header className="ddm-header">
          <div className="ddm-header-top">
            <div className="ddm-name-block">
              <span className="ddm-flag">{p.nationalFlag || ''}</span>
              <h2 className="ddm-name">{p.name || '名前不明'}</h2>
              <span className="ddm-level">Lv{p.level || '?'}</span>
            </div>
            {score != null && (
              <div className={`ddm-score ${scoreClass}`}>
                <span className="ddm-score-num">{score.toFixed(0)}</span>
                <span className="ddm-score-label">脅威度</span>
              </div>
            )}
          </div>
          <div className="ddm-header-sub">
            <span className="ddm-sub-chip">Tower {towerLabel}</span>
            <span className="ddm-sub-chip ddm-chip-win">防衛 {defender.winCount || 0}</span>
            <span className="ddm-sub-chip ddm-chip-remain">残り {defender.remainDefenceCount || 0}</span>
            {parseInt(defender.remainDefenceCount, 10) === 5 && (
              <span className="ddm-sub-chip ddm-chip-main">メイン</span>
            )}
            {defender.progressInBattle && (
              <span className="ddm-sub-chip ddm-chip-battle">戦闘中</span>
            )}
          </div>
        </header>

        <nav className="ddm-tabs">
          {[
            { key: 'tower', label: 'タワー' },
            { key: 'units', label: `レンジャー (${units.length})` },
            { key: 'player', label: 'プレイヤー' },
          ].map((t) => (
            <button
              key={t.key}
              className={`ddm-tab ${tab === t.key ? 'ddm-tab-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="ddm-body">
          {tab === 'tower' && (
            <div className="ddm-section">
              <h3 className="ddm-section-title">タワー性能</h3>
              <StatBar label="HP" value={machine.hp || 0} max={150000000} color="#5ee08a" display={fmt(machine.hp)} />
              <StatBar label="攻撃力" value={machine.attack || 0} max={30000000} color="#ff7a6b" display={fmt(machine.attack)} />
              <StatBar label="チャージ速度" value={machine.chargeSpeed || 0} max={10} color="#5cb3ff" display={machine.chargeSpeed != null ? Number(machine.chargeSpeed).toFixed(2) : '-'} />
              <StatBar label="ユニットHP倍率" value={machine.unitHpRate || 0} max={3} color="#ffd95c" display={machine.unitHpRate != null ? `×${Number(machine.unitHpRate).toFixed(2)}` : '-'} />
              <StatBar label="ユニット攻撃倍率" value={machine.unitAttackRate || 0} max={3} color="#ffd95c" display={machine.unitAttackRate != null ? `×${Number(machine.unitAttackRate).toFixed(2)}` : '-'} />
              <StatBar label="最大エネルギー" value={machine.maxEnergy || 0} max={5} color="#b48cff" display={machine.maxEnergy != null ? Number(machine.maxEnergy).toFixed(1) : '-'} />
              <StatBar label="エネルギー生産" value={machine.energyProduce || 0} max={5} color="#b48cff" display={machine.energyProduce != null ? Number(machine.energyProduce).toFixed(2) : '-'} />

              <h3 className="ddm-section-title">強化レベル</h3>
              <div className="ddm-upgrade-grid">
                {[
                  ['タワー攻撃', upgradeLv.machineAttackLevel],
                  ['タワーHP', upgradeLv.machineHpLevel],
                  ['チャージ速度', upgradeLv.machineChargeSpeedLevel],
                  ['攻撃範囲', upgradeLv.machineAttackScopeLevel],
                  ['エネルギー上限', upgradeLv.energyLimitLevel],
                  ['エネルギー生産', upgradeLv.energyProductionLevel],
                  ['初期エネルギー', upgradeLv.initialEnergyLevel],
                  ['エネ獲得', upgradeLv.acquireEnergyLevel],
                  ['ユニットHP', upgradeLv.unitHpLevel],
                  ['ユニット攻撃', upgradeLv.unitAttackLevel],
                  ['STR被ダメ軽減', upgradeLv.decreaseDamageForStrLevel],
                  ['AGIクリティカル', upgradeLv.increaseCriticalForAgiLevel],
                  ['INT magicダメ', upgradeLv.increaseMagicalDamageForIntLevel],
                  ['ラッキー確率', upgradeLv.luckyChanceRateLevel],
                  ['ラッキーミサイル', upgradeLv.luckyMissileAbsDamageRateLevel],
                  ['タワー抵抗', upgradeLv.towerResistRateLevel],
                ].map(([label, lv]) => (
                  <div key={label} className="ddm-upgrade-cell">
                    <span className="ddm-upgrade-label">{label}</span>
                    <span className="ddm-upgrade-value">{lv != null ? lv : '-'}</span>
                  </div>
                ))}
              </div>

              {upgrade.machineAttack != null && (
                <>
                  <h3 className="ddm-section-title">強化ステータス（実数値）</h3>
                  <div className="ddm-upgrade-grid">
                    {[
                      ['タワー攻撃力', fmt(upgrade.machineAttack)],
                      ['タワーHP', fmt(upgrade.machineHp)],
                      ['チャージ速度', upgrade.machineCharge != null ? Number(upgrade.machineCharge).toFixed(2) : '-'],
                      ['エネルギー上限', fmt(upgrade.energyMax)],
                      ['エネルギー生産', upgrade.energyProduce != null ? Number(upgrade.energyProduce).toFixed(2) : '-'],
                      ['ユニットHP倍率', upgrade.unitHpRate != null ? `×${Number(upgrade.unitHpRate).toFixed(2)}` : '-'],
                      ['ユニット攻撃倍率', upgrade.unitAttackRate != null ? `×${Number(upgrade.unitAttackRate).toFixed(2)}` : '-'],
                      ['ラッキー確率', upgrade.rewardLuckyChanceRate != null ? `${Number(upgrade.rewardLuckyChanceRate).toFixed(1)}%` : '-'],
                      ['タワー抵抗', upgrade.rewardTowerResistRate != null ? `${Number(upgrade.rewardTowerResistRate).toFixed(1)}%` : '-'],
                    ].map(([label, val]) => (
                      <div key={label} className="ddm-upgrade-cell">
                        <span className="ddm-upgrade-label">{label}</span>
                        <span className="ddm-upgrade-value ddm-upgrade-stat">{val}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'units' && (
            <div className="ddm-section">
              {units.length === 0 ? (
                <p className="ddm-empty">デッキ情報がありません。</p>
              ) : (
                units.map((unit, i) => (
                  <UnitRow key={i} unit={unit} index={i} skillTexts={skillTexts} />
                ))
              )}
            </div>
          )}

          {tab === 'player' && (
            <div className="ddm-section">
              <h3 className="ddm-section-title">プレイヤー情報</h3>
              <div className="ddm-player-grid">
                {[
                  ['プレイヤー名', p.name || '-'],
                  ['レベル', p.level || '-'],
                  ['国籍', p.nationalFlag || '-'],
                  [
                    '権限',
                    p.guildAuthority === 'ADMIN'
                      ? '管理者'
                      : p.guildAuthority === 'NORMAL'
                        ? 'メンバー'
                        : p.guildAuthority || '-',
                  ],
                  ['加入日', admitted],
                  ['最終アクセス', lastAccess],
                  ['デッキID', defender.deckId || '-'],
                  ['使用チーム', deck.useTeamNo || p.useTeamNo || '-'],
                  ['ゲスト', p.guest ? 'はい' : 'いいえ'],
                  ['AIユーザー', defender.isAIUser ? 'はい' : 'いいえ'],
                  ['撤退済み', defender.withdrawal ? 'はい' : 'いいえ'],
                  ['UID', p.uid ? `${String(p.uid).slice(0, 12)}…` : '-'],
                ].map(([label, val]) => (
                  <div key={label} className="ddm-player-cell">
                    <span className="ddm-player-label">{label}</span>
                    <span className="ddm-player-value">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DefenderDetailModal;
